import { useState, useEffect, useCallback, useRef } from 'react';
import { Switch, Modal } from 'antd';
import {
  RefreshCw,
  DollarSign,
  Plus,
  X,
  Lock,
  Building2,
  UserCircle,
  Truck,
  Loader2,
  AlertTriangle,
  Pencil,
  RotateCcw,
  Check,
  Package,
  Zap,
} from 'lucide-react';
import api from '../../../services/api';
import { driverPaymentAPI } from '../../../services/api';
import { driverYardCreditApi, type YardTaskContext } from '../../../services/driverYardCreditApi';
import { yardColor } from '../../../utils/yardCreditColors';
import { cn } from '../../../lib/utils';

/* ── Types ── */

interface DraftPaymentExtra {
  id: number;
  type: string;
  description: string | null;
  amount: number;
  quantity: number | null;
  unit: string | null;
  source: 'auto' | 'manual';
  salary_extra_fee_id: number | null;
}

interface DraftPayment {
  id: number;
  payment_number: string;
  driver_name: string | null;
  driver_type: string | null;
  payment_role: 'driver' | 'truck_owner';
  base_rate: number;
  extra_rate: number;
  total_amount: number;
  status: string;
  is_base_rate_manual?: boolean;
  extras: DraftPaymentExtra[];
  rate_match_note?: string | null;
  // Salary context
  salary_task_type?: string;
  origin_zone_name?: string;
  destination_zone_name?: string;
  bobtail_from_zone_name?: string;
  bobtail_to_zone_name?: string;
  is_bobtail_leg?: boolean;
  return_bobtail_from_zone_name?: string;
  return_bobtail_to_zone_name?: string;
  is_return_bobtail_leg?: boolean;
  terminal_enter_time?: string | null;
  terminal_exit_time?: string | null;
  wh_arrive_time?: string | null;
  wh_exit_time?: string | null;
}

interface SalaryTemplate {
  id: number;
  category: string;
  name: string;
  driverType: string;
  feeType: string;
  amount: number;
  unitValue: number;
  unitType: string | null;
  graceValue: number;
  graceUnit: string;
  isSystem: boolean;
}

const RATE_CARD_TYPES = [
  { key: 'Overweight', label: 'Overweight', desc: 'Per route surcharge' },
  { key: 'Bobtail', label: 'Bobtail', desc: 'Deadhead fee' },
  // The empty run AFTER the task — its own zone pair, so its own row.
  { key: 'Bobtail Return', label: 'Bobtail Return', desc: 'Deadhead fee (after task)' },
];

/* ── Component ── */

interface SalaryPanelProps {
  taskDbId: number;
  hasDriver: boolean;
  refreshKey?: number; // increment to force re-fetch (e.g. after task save)
}

export function SalaryPanel({ taskDbId, hasDriver, refreshKey }: SalaryPanelProps) {
  const [payments, setPayments] = useState<DraftPayment[]>([]);
  const [templates, setTemplates] = useState<SalaryTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  // Guard against stale responses when task switches rapidly
  const requestIdRef = useRef(0);
  const [syncing, setSyncing] = useState(false);
  const [activeRole, setActiveRole] = useState<'driver' | 'truck_owner'>('driver');
  const [togglingKey, setTogglingKey] = useState<string | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({ open: false, title: '', message: '', onConfirm: () => {} });
  const [adHocDesc, setAdHocDesc] = useState('');
  const [adHocAmount, setAdHocAmount] = useState('');
  const [addingAdHoc, setAddingAdHoc] = useState(false);
  const [removingId, setRemovingId] = useState<number | null>(null);
  const [yardCtx, setYardCtx] = useState<YardTaskContext | null>(null);
  const [directPaying, setDirectPaying] = useState(false);

  const fetchData = useCallback(async (showSpinner = true) => {
    const thisRequest = ++requestIdRef.current;
    if (showSpinner) setLoading(true);
    try {
      const tplRes = await api.get('/salary-extra-fees').catch(() => ({ data: [] }));
      if (thisRequest !== requestIdRef.current) return;
      const raw = (tplRes.data || []) as Record<string, unknown>[];
      setTemplates(raw.map(r => ({
        id: r.id as number, category: r.category as string, name: r.name as string,
        driverType: (r.driver_type as string) || 'COMPANY', feeType: (r.fee_type as string) || 'flat',
        amount: Number(r.amount ?? 0), unitValue: Number(r.unit_value ?? 1),
        unitType: (r.unit_type as string) || null, graceValue: Number(r.grace_value ?? 0),
        graceUnit: (r.grace_unit as string) || 'hour',
        isSystem: (r.is_system as boolean) ?? false,
      })));

      // Yard-storage credit context — a held leg1 has no payment, so this is the
      // only way to know the task is a yard leg (and to get the container for direct-pay).
      try {
        const ctx = await driverYardCreditApi.getTaskContext(taskDbId);
        if (thisRequest !== requestIdRef.current) return;
        setYardCtx(ctx);
      } catch { if (thisRequest === requestIdRef.current) setYardCtx(null); }

      let drafts: DraftPayment[] = [];

      // First try GET (doesn't overwrite manually adjusted amounts)
      try {
        const res = await api.get(`/draft-driver-payments/task/${taskDbId}`);
        drafts = (res.data || []) as DraftPayment[];
      } catch { /* no drafts */ }

      // Only use ensure if no drafts exist AND task has a driver
      // (ensure creates + syncs, which would overwrite manual adjustments)
      if (drafts.length === 0 && hasDriver) {
        try {
          const ensureRes = await api.post(`/draft-driver-payments/task/${taskDbId}/ensure`);
          drafts = (ensureRes.data || []) as DraftPayment[];
        } catch { /* ignore */ }
      }

      // Final fallback: check confirmed payments
      if (drafts.length === 0) {
        try {
          const payRes = await driverPaymentAPI.getAll({ taskId: taskDbId });
          drafts = (payRes.data || []).map((p: Record<string, unknown>) => ({
            id: p.id as number, payment_number: (p.payment_number || '') as string,
            driver_name: p.driver_name as string | null, driver_type: p.driver_type as string | null,
            payment_role: ((p.payment_role as string) || 'driver') as 'driver' | 'truck_owner',
            base_rate: parseFloat(String(p.base_rate)) || 0, extra_rate: parseFloat(String(p.extra_rate)) || 0,
            total_amount: parseFloat(String(p.total_amount)) || 0,
            status: p.status as string, extras: (p.extras || []) as DraftPaymentExtra[],
            rate_match_note: (p.rate_match_note as string) || null,
          }));
        } catch { /* nothing */ }
      }

      // Ignore stale response if task switched while request was in flight
      if (thisRequest !== requestIdRef.current) return;
      setPayments(drafts);
    } catch (err) {
      if (thisRequest !== requestIdRef.current) return;
      console.error('SalaryPanel fetch error:', err);
    } finally {
      if (thisRequest !== requestIdRef.current) return;
      if (showSpinner) setLoading(false);
    }
  }, [taskDbId, hasDriver, refreshKey]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSync = async () => {
    if (payments.length === 0) return;
    setSyncing(true);
    try {
      // Sync all payments
      await Promise.all(payments.map(p =>
        api.post(`/draft-driver-payments/${p.id}/sync`)
      ));
      // Refresh to get latest state
      await fetchData(false);
    } finally {
      setSyncing(false);
    }
  };

  // After initial data load, auto-fix mismatched extras between sibling payments.
  // Uses a ref to prevent repeated runs.
  const hasAutoFixed = useRef(false);
  useEffect(() => {
    if (payments.length < 2 || templates.length === 0 || hasAutoFixed.current) return;

    const fixExtras = async () => {
      hasAutoFixed.current = true;

      // Collect all enabled template extras (by category) from all payments
      const enabledCategories = new Set<string>();
      for (const p of payments) {
        const extras = Array.isArray(p.extras) ? p.extras : [];
        for (const e of extras) {
          if (e.salary_extra_fee_id) {
            const tpl = templates.find(t => t.id === e.salary_extra_fee_id);
            if (tpl) enabledCategories.add(tpl.category);
            else {
              const byName = templates.find(t => t.name.toLowerCase() === e.type.toLowerCase());
              if (byName) enabledCategories.add(byName.category);
            }
          }
        }
      }
      if (enabledCategories.size === 0) return;

      let fixed = false;
      for (const p of payments) {
        const dt = p.driver_type === 'owner-operator' ? 'OWNER_OPERATOR' : 'COMPANY';
        const extras = Array.isArray(p.extras) ? p.extras : [];

        for (const cat of enabledCategories) {
          const correctTpl = templates.find(t => t.category === cat && t.driverType === dt);
          if (!correctTpl) continue;

          const hasCorrect = extras.some(e => e.salary_extra_fee_id === correctTpl.id);
          if (!hasCorrect) {
            try {
              await api.post(`/draft-driver-payments/${p.id}/toggle-extra`, {
                templateId: correctTpl.id, enabled: true,
              });
              fixed = true;
            } catch { /* ignore */ }
          }
        }

        // Clean up extras with wrong template IDs (COMPANY ID on OWNER payment or vice versa)
        for (const e of extras) {
          if (!e.salary_extra_fee_id) continue;
          const tpl = templates.find(t => t.id === e.salary_extra_fee_id);
          if (tpl && tpl.driverType !== dt) {
            try {
              await api.post(`/draft-driver-payments/${p.id}/toggle-extra`, {
                templateId: e.salary_extra_fee_id, enabled: false,
              });
              fixed = true;
            } catch { /* ignore */ }
          }
        }
      }

      if (fixed) await fetchData(false);
    };

    fixExtras();
  }, [payments.length, templates.length]);

  // Switch this container to direct pay (skip yard credit → normal segment pricing).
  const handleDirectPay = async () => {
    if (!yardCtx?.containerId) return;
    setDirectPaying(true);
    try {
      await driverYardCreditApi.directPay(yardCtx.containerId);
      await fetchData(false);
    } catch (err) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      console.warn('Yard direct-pay failed:', msg || err);
    } finally { setDirectPaying(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  // Held yard leg1 (terminal→yard) is ALWAYS a held $0 credit in credit mode — show the
  // credit card whether the task is in-progress (has a $0 draft) or completed (no payment),
  // and regardless of driver assignment.
  if (yardCtx?.isYardLeg1) {
    return (
      <div className="bg-indigo-50 rounded-xl border border-indigo-200 p-5">
        <div className="flex items-start gap-2.5">
          <Package className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-indigo-800 inline-flex items-center gap-1.5">
              {yardCtx.yardName && (
                <span className={`w-2 h-2 rounded-full ${yardColor(yardCtx.yardColorIndex).dot}`} aria-hidden="true" />
              )}
              Stored at {yardCtx.yardName || 'yard'} · credit +1
            </p>
            <p className="text-xs text-indigo-600 mt-1 leading-relaxed">
              This terminal→yard leg is held at $0 — its pay folds into the terminal→WH
              through rate when the container is delivered. Driver credit
              at {yardCtx.yardName || 'this yard'}: <b>{yardCtx.balance}</b>
              {' '}(credits are counted per yard).
            </p>
            <button
              onClick={handleDirectPay}
              disabled={directPaying}
              className="mt-3 px-3 py-1.5 text-xs font-medium text-indigo-700 bg-white border border-indigo-300 rounded-lg hover:bg-indigo-100 disabled:opacity-50"
              title="Pay this leg normally (terminal→yard) instead of holding the credit"
            >
              {directPaying ? 'Switching…' : 'Pay directly (terminal→yard)'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (payments.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
        <DollarSign className="w-8 h-8 text-gray-300 mx-auto mb-3" />
        <p className="text-sm text-gray-500">No salary data yet</p>
        <p className="text-xs text-gray-400 mt-1">Waiting for route and driver assignment</p>
      </div>
    );
  }

  const isDual = payments.length > 1;
  const driverPayment = payments.find(p => p.payment_role === 'driver');
  const ownerPayment = payments.find(p => p.payment_role === 'truck_owner');
  const activePayment = isDual
    ? (activeRole === 'truck_owner' ? ownerPayment : driverPayment) || payments[0]
    : payments[0];
  const grandTotal = payments.reduce((s, p) => s + (parseFloat(String(p.total_amount)) || 0), 0);

  return (
    <div className="space-y-3">
      {/* Header row: status + refresh */}
      <div className="flex items-center justify-end">
        <div className="flex items-center gap-2">
          <span className={cn(
            'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
            activePayment.status === 'draft' ? 'bg-amber-50 text-amber-700 border border-amber-200'
            : activePayment.status === 'pending' ? 'bg-yellow-50 text-yellow-700 border border-yellow-200'
            : activePayment.status === 'approved' ? 'bg-green-50 text-green-700 border border-green-200'
            : activePayment.status === 'paid' ? 'bg-blue-50 text-blue-700 border border-blue-200'
            : 'bg-gray-50 text-gray-600 border border-gray-200'
          )}>
            {activePayment.status === 'draft' ? 'Draft' : activePayment.status}
          </span>
          {activePayment.payment_number && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200">
              {activePayment.payment_number}
            </span>
          )}
          {activePayment.status === 'draft' && (
            <button
              onClick={handleSync}
              disabled={syncing}
              className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-3 h-3 ${syncing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          )}
        </div>
      </div>

      {/* Yard through-pricing strip (leg2 priced terminal→WH) */}
      {activePayment?.rate_match_note?.startsWith('Yard direct') && (
        <div className="flex items-center justify-between gap-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg">
          <div className="flex items-center gap-1.5 text-[11px] text-emerald-700 leading-tight">
            <Zap className="w-3.5 h-3.5 flex-shrink-0" />
            <span>Direct pricing — terminal→WH through rate (yard credit consumed).</span>
          </div>
          {yardCtx?.containerId && (
            <button
              onClick={handleDirectPay}
              disabled={directPaying}
              className="flex-shrink-0 px-2 py-1 text-[11px] font-medium text-emerald-700 bg-white border border-emerald-300 rounded hover:bg-emerald-100 disabled:opacity-50"
              title="Switch this container to normal segment pricing"
            >
              {directPaying ? 'Switching…' : 'Pay directly'}
            </button>
          )}
        </div>
      )}

      {/* Dual payment tabs */}
      {isDual && (
        <div className="flex rounded-lg bg-gray-100 p-0.5 gap-0.5">
          <button
            onClick={() => setActiveRole('driver')}
            className={cn(
              'flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-xs font-semibold transition-all',
              activeRole === 'driver'
                ? 'bg-white text-blue-700 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            )}
          >
            <Building2 className="h-3.5 w-3.5" />
            Driver
            {driverPayment && (
              <span className={cn(
                'ml-1 tabular-nums',
                activeRole === 'driver' ? 'text-blue-600' : 'text-gray-400'
              )}>
                ${parseFloat(String(driverPayment.total_amount)).toFixed(0)}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveRole('truck_owner')}
            className={cn(
              'flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-xs font-semibold transition-all',
              activeRole === 'truck_owner'
                ? 'bg-white text-orange-700 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            )}
          >
            <Truck className="h-3.5 w-3.5" />
            Truck Owner
            {ownerPayment && (
              <span className={cn(
                'ml-1 tabular-nums',
                activeRole === 'truck_owner' ? 'text-orange-600' : 'text-gray-400'
              )}>
                ${parseFloat(String(ownerPayment.total_amount)).toFixed(0)}
              </span>
            )}
          </button>
        </div>
      )}

      {/* Active payment content */}
      <PaymentContent
        payment={activePayment}
        allPayments={payments}
        allPaymentIds={payments.map(p => p.id)}
        templates={templates}
        togglingKey={togglingKey}
        setTogglingKey={setTogglingKey}
        confirmDialog={confirmDialog}
        setConfirmDialog={setConfirmDialog}
        adHocDesc={adHocDesc}
        setAdHocDesc={setAdHocDesc}
        adHocAmount={adHocAmount}
        setAdHocAmount={setAdHocAmount}
        addingAdHoc={addingAdHoc}
        setAddingAdHoc={setAddingAdHoc}
        removingId={removingId}
        setRemovingId={setRemovingId}
        fetchData={fetchData}
      />

      {/* Confirmation dialog */}
      <Modal
        open={confirmDialog.open}
        title={confirmDialog.title}
        onOk={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog(prev => ({ ...prev, open: false }))}
        okText="Yes, disable"
        cancelText="Cancel"
        okButtonProps={{ danger: true }}
        centered
      >
        <p className="text-sm text-gray-600">{confirmDialog.message}</p>
      </Modal>
    </div>
  );
}

/* ── Payment Content (single payment view) ── */

interface PaymentContentProps {
  payment: DraftPayment;
  allPayments: DraftPayment[];
  allPaymentIds: number[];
  templates: SalaryTemplate[];
  togglingKey: string | null;
  setTogglingKey: (k: string | null) => void;
  confirmDialog: { open: boolean; title: string; message: string; onConfirm: () => void };
  setConfirmDialog: React.Dispatch<React.SetStateAction<{ open: boolean; title: string; message: string; onConfirm: () => void }>>;
  adHocDesc: string;
  setAdHocDesc: (v: string) => void;
  adHocAmount: string;
  setAdHocAmount: (v: string) => void;
  addingAdHoc: boolean;
  setAddingAdHoc: (v: boolean) => void;
  removingId: number | null;
  setRemovingId: (v: number | null) => void;
  fetchData: (showSpinner?: boolean) => Promise<void>;
}

function PaymentContent({
  payment, allPayments, allPaymentIds, templates,
  togglingKey, setTogglingKey,
  setConfirmDialog,
  adHocDesc, setAdHocDesc, adHocAmount, setAdHocAmount,
  addingAdHoc, setAddingAdHoc, removingId, setRemovingId,
  fetchData,
}: PaymentContentProps) {
  const isDraft = payment.status === 'draft';
  const canEditBaseRate = payment.status === 'draft' || payment.status === 'pending';
  const isOwner = payment.payment_role === 'truck_owner';
  const isManual = !!payment.is_base_rate_manual;

  // Inline base_rate editing
  const [editingBaseRate, setEditingBaseRate] = useState(false);
  const [baseRateInput, setBaseRateInput] = useState('');
  const [savingBaseRate, setSavingBaseRate] = useState(false);

  const handleSaveBaseRate = async () => {
    const amt = parseFloat(baseRateInput);
    if (isNaN(amt) || amt < 0) return;
    setSavingBaseRate(true);
    try {
      await api.patch(`/draft-driver-payments/${payment.id}/base-rate`, { base_rate: amt });
      setEditingBaseRate(false);
      await fetchData(false);
    } catch { /* ignore */ }
    finally { setSavingBaseRate(false); }
  };

  const handleResetBaseRate = async () => {
    setSavingBaseRate(true);
    try {
      await api.post(`/draft-driver-payments/${payment.id}/reset-base-rate`);
      await fetchData(false);
    } catch (err) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      console.warn('Reset base rate failed:', msg || err);
    } finally { setSavingBaseRate(false); }
  };

  // Toggle extra on ALL sibling payments (keeps Driver & Owner in sync).
  // For template-based extras, each payment has a different templateId per driver type,
  // so we look up by category to find the correct template for each payment.
  const toggleExtraOnAll = async (body: Record<string, unknown>) => {
    if (body.templateId) {
      const srcTpl = templates.find(t => t.id === body.templateId);
      if (!srcTpl) return;
      await Promise.all(allPayments.map(p => {
        const dt = p.driver_type === 'owner-operator' ? 'OWNER_OPERATOR' : 'COMPANY';
        const tpl = templates.find(t => t.category === srcTpl.category && t.driverType === dt);
        if (!tpl) return Promise.resolve();
        return api.post(`/draft-driver-payments/${p.id}/toggle-extra`, { ...body, templateId: tpl.id });
      }));
    } else {
      // Rate card extras (Overweight, Bobtail) — same type key for both driver types
      await Promise.all(allPaymentIds.map(pid =>
        api.post(`/draft-driver-payments/${pid}/toggle-extra`, body)
      ));
    }
  };
  const baseRate = parseFloat(String(payment.base_rate)) || 0;
  const extraRate = parseFloat(String(payment.extra_rate)) || 0;
  const total = parseFloat(String(payment.total_amount)) || 0;
  const extras = Array.isArray(payment.extras) ? payment.extras : [];
  const isOwnerType = payment.driver_type === 'owner-operator';
  const accentColor = isOwnerType ? '#F97316' : '#2563EB';

  // Merge templates with actual extras
  const driverTypeForTpl = payment.driver_type === 'owner-operator' ? 'OWNER_OPERATOR' : 'COMPANY';
  const relevantTpls = templates.filter(t => t.driverType === driverTypeForTpl);

  const allTemplateRows = relevantTpls.map(tpl => ({
    template: tpl,
    actual: extras.find(e =>
      (e.salary_extra_fee_id && e.salary_extra_fee_id === tpl.id) ||
      (e.source === 'auto' && !e.salary_extra_fee_id &&
        (e.type.toLowerCase().includes(tpl.category.replace('_', ' ')) || tpl.name.toLowerCase().includes(e.type.toLowerCase())))
    ) || null,
  }));
  const systemRows = allTemplateRows.filter(r => r.template.isSystem);
  const customRows = allTemplateRows.filter(r => !r.template.isSystem);

  const rateCardRows = RATE_CARD_TYPES.map(rc => ({
    ...rc,
    actual: extras.find(e => (e.source === 'auto' || e.source === 'enabled') && e.type === rc.key) || null,
  }));

  const matchedIds = new Set<number>();
  allTemplateRows.forEach(r => { if (r.actual) matchedIds.add(r.actual.id); });
  rateCardRows.forEach(r => { if (r.actual) matchedIds.add(r.actual.id); });

  // Backend marks a failed yard through-price with this prefix (never 'Yard direct',
  // which means it succeeded).
  const yardThroughFailed = payment.rate_match_note?.startsWith('No yard through rate') ?? false;

  const manualExtras = extras.filter(e => e.source === 'manual');
  const otherAutoExtras = extras.filter(e => e.source === 'auto' && !matchedIds.has(e.id));
  const adHocTotal = manualExtras.reduce((s, e) => s + (parseFloat(String(e.amount)) || 0), 0);

  return (
    <>
      {/* Recipient name */}
      {payment.driver_name && (
        <div className="text-xs text-gray-500 flex items-center gap-1">
          {payment.driver_type === 'owner-operator'
            ? <Truck className="w-3 h-3 text-orange-500" />
            : <UserCircle className="w-3 h-3 text-blue-500" />}
          {payment.driver_name}
          <span className="text-gray-300 mx-1">·</span>
          <span className={cn(
            'px-1.5 py-0.5 rounded text-[10px] font-medium',
            payment.driver_type === 'owner-operator' ? 'bg-orange-50 text-orange-600' : 'bg-blue-50 text-blue-600'
          )}>
            {payment.driver_type === 'owner-operator' ? 'Owner-Op' : 'Company'}
          </span>
        </div>
      )}

      {/* Base Rate Card */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: isOwner ? '#FFF7ED' : '#EFF6FF' }}>
              <DollarSign className="w-4 h-4" style={{ color: accentColor }} />
            </div>
            <div>
              <span className="text-sm text-gray-600">Base Rate</span>
              {payment.origin_zone_name && payment.destination_zone_name && (
                <div className="text-xs text-gray-400 mt-0.5">
                  {payment.origin_zone_name} → {payment.destination_zone_name}
                  {payment.salary_task_type && (
                    <span className="ml-1.5 px-1.5 py-0.5 bg-gray-100 rounded text-[10px] font-medium text-gray-500">
                      {payment.salary_task_type.replace(/_/g, ' ')}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
          {/* A failed yard through-price must show even when the fallback DID find a
              segment rate — that's the silent-underpay case (leg1 credit stays open). */}
          {payment.rate_match_note && (baseRate === 0 || yardThroughFailed) && !isManual && (
            <div className={cn(
              'flex items-start gap-1 px-2 py-1 bg-amber-50 border border-amber-200 rounded-lg',
              yardThroughFailed ? 'max-w-[320px]' : 'max-w-[200px]'
            )}>
              <AlertTriangle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0 mt-px" />
              <span className="text-[11px] text-amber-700 leading-tight">{payment.rate_match_note}</span>
            </div>
          )}
          <div className="flex items-center gap-2">
            {isManual && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-orange-100 text-orange-700 border border-orange-200">
                <Pencil className="w-2.5 h-2.5" />Manual
              </span>
            )}
            {editingBaseRate ? (
              <div className="flex items-center gap-1">
                <span className="text-sm text-gray-400">$</span>
                <input
                  type="number" min="0" step="0.01"
                  autoFocus
                  value={baseRateInput}
                  onChange={e => setBaseRateInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleSaveBaseRate(); if (e.key === 'Escape') setEditingBaseRate(false); }}
                  className="w-24 px-2 py-1 text-right text-lg font-bold border border-blue-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500"
                  disabled={savingBaseRate}
                />
                <button onClick={handleSaveBaseRate} disabled={savingBaseRate} className="p-1 text-green-600 hover:bg-green-50 rounded">
                  <Check className="w-4 h-4" />
                </button>
                <button onClick={() => setEditingBaseRate(false)} className="p-1 text-gray-400 hover:bg-gray-100 rounded">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-1 group">
                <span className="text-xl font-bold" style={{ color: accentColor }}>
                  ${baseRate.toFixed(2)}
                </span>
                {canEditBaseRate && (
                  <button
                    onClick={() => { setBaseRateInput(String(baseRate)); setEditingBaseRate(true); }}
                    className="p-1 text-gray-300 hover:text-blue-500 opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Edit base rate"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                )}
                {canEditBaseRate && isManual && (
                  <button
                    onClick={handleResetBaseRate}
                    disabled={savingBaseRate}
                    className="p-1 text-gray-300 hover:text-orange-500 opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Reset to auto-calculated rate"
                  >
                    <RotateCcw className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Extra Fees Card */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-4">
        {/* Rate Card Extras */}
        <div>
          <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Rate Card Extras</div>
          <div className="space-y-1.5">
            {rateCardRows.map(({ key, label, desc, actual }) => {
              const isActive = actual !== null;
              const rcToggleKey = `rc-${payment.id}-${key}`;
              let contextDesc = isActive && actual?.description ? actual.description : desc;
              // Both deadhead legs show the zone pair they were priced from — the
              // amount comes off a zone-pair rate card, so without it the figure
              // cannot be checked. The return leg uses a different pair than the
              // inbound one, hence its own fields.
              if (key === 'Bobtail' && payment.bobtail_from_zone_name && payment.bobtail_to_zone_name) {
                contextDesc = `${payment.bobtail_from_zone_name} → ${payment.bobtail_to_zone_name}`;
              }
              if (key === 'Bobtail Return' && payment.return_bobtail_from_zone_name && payment.return_bobtail_to_zone_name) {
                contextDesc = `${payment.return_bobtail_from_zone_name} → ${payment.return_bobtail_to_zone_name}`;
              }
              return (
                <ExtraRow
                  key={`rc-${key}`}
                  label={label}
                  desc={contextDesc}
                  amount={isActive && actual ? parseFloat(String(actual.amount)) : 0}
                  isActive={isActive}
                  isDraft={isDraft}
                  isToggling={togglingKey === rcToggleKey}
                  onToggle={(checked) => {
                    const doToggle = async () => {
                      setTogglingKey(rcToggleKey);
                      try {
                        await toggleExtraOnAll({ type: key, enabled: checked, description: desc });
                        await fetchData(false);
                      } catch (e) { console.error(e); }
                      finally { setTogglingKey(null); }
                    };
                    if (isActive && !checked && actual?.source === 'auto') {
                      setConfirmDialog({
                        open: true, title: `Disable ${label}?`,
                        message: `System detected ${label.toLowerCase()} for this task. Are you sure you want to disable it?`,
                        onConfirm: () => { setConfirmDialog(prev => ({ ...prev, open: false })); doToggle(); },
                      });
                    } else { doToggle(); }
                  }}
                />
              );
            })}
            {otherAutoExtras.map(extra => (
              <ExtraRow
                key={extra.id}
                label={extra.type}
                desc={extra.description || undefined}
                amount={parseFloat(String(extra.amount))}
                isActive
                isDraft={false}
                isToggling={false}
              />
            ))}
          </div>
        </div>

        {/* System Extras */}
        {systemRows.length > 0 && (
          <div>
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">System Extras</div>
            <div className="space-y-1.5">
              {systemRows.map(({ template: tpl, actual }) => {
                const isActive = actual !== null;
                const toggleKey = `sys-${payment.id}-${tpl.id}`;
                const unitLabel = tpl.feeType === 'per_unit' && tpl.unitType
                  ? `$${tpl.amount}/${tpl.unitValue}${tpl.unitType}` : `$${tpl.amount} flat`;
                const graceLabel = tpl.graceValue > 0
                  ? `${tpl.graceValue} ${tpl.graceUnit}${tpl.graceValue > 1 ? 's' : ''} grace` : undefined;

                // Timestamp context for waiting fees
                let timelineDesc: string | undefined;
                const formatT = (ts: string | null | undefined) => ts ? new Date(ts).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false }) : '—';
                const durStr = (s: string | null | undefined, e: string | null | undefined) => {
                  if (!s || !e) return null;
                  const m = Math.round((new Date(e).getTime() - new Date(s).getTime()) / 60000);
                  if (m <= 0) return null;
                  return m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`;
                };
                if (tpl.category === 'port_waiting' && payment.terminal_enter_time) {
                  const d = durStr(payment.terminal_enter_time, payment.terminal_exit_time);
                  timelineDesc = `${formatT(payment.terminal_enter_time)} → ${formatT(payment.terminal_exit_time)}${d ? ` · ${d}` : ''}`;
                } else if (tpl.category === 'wh_waiting' && payment.wh_arrive_time) {
                  const d = durStr(payment.wh_arrive_time, payment.wh_exit_time);
                  timelineDesc = `${formatT(payment.wh_arrive_time)} → ${formatT(payment.wh_exit_time)}${d ? ` · ${d}` : ''}`;
                }

                return (
                  <ExtraRow
                    key={`tpl-${tpl.id}`}
                    label={tpl.name}
                    desc={unitLabel}
                    graceLabel={graceLabel}
                    detailDesc={timelineDesc || (isActive && actual?.description ? actual.description : undefined)}
                    amount={isActive && actual ? parseFloat(String(actual.amount)) : tpl.amount}
                    isActive={isActive}
                    isDraft={isDraft}
                    isToggling={togglingKey === toggleKey}
                    onToggle={(checked) => {
                      const doToggle = async () => {
                        setTogglingKey(toggleKey);
                        try {
                          await toggleExtraOnAll({ templateId: tpl.id, enabled: checked });
                          await fetchData(false);
                        } catch (e) { console.error(e); }
                        finally { setTogglingKey(null); }
                      };
                      if (isActive && !checked && actual?.source === 'auto') {
                        setConfirmDialog({
                          open: true, title: `Disable ${tpl.name}?`,
                          message: `System detected ${tpl.name.toLowerCase()} for this task. Are you sure you want to disable it?`,
                          onConfirm: () => { setConfirmDialog(prev => ({ ...prev, open: false })); doToggle(); },
                        });
                      } else { doToggle(); }
                    }}
                  />
                );
              })}
            </div>
          </div>
        )}

        {/* Custom Extras */}
        {customRows.length > 0 && (
          <div>
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Custom Extras</div>
            <div className="space-y-1.5">
              {customRows.map(({ template: tpl, actual }) => {
                const isActive = actual !== null;
                const toggleKey = `cust-${payment.id}-${tpl.id}`;
                const unitLabel = tpl.feeType === 'per_unit' && tpl.unitType
                  ? `$${tpl.amount}/${tpl.unitValue}${tpl.unitType}` : `$${tpl.amount} flat`;
                return (
                  <ExtraRow
                    key={`cust-${tpl.id}`}
                    label={tpl.name}
                    desc={unitLabel}
                    amount={isActive && actual ? parseFloat(String(actual.amount)) : tpl.amount}
                    isActive={isActive}
                    isDraft={isDraft}
                    isToggling={togglingKey === toggleKey}
                    onToggle={async (checked) => {
                      setTogglingKey(toggleKey);
                      try {
                        await toggleExtraOnAll({ templateId: tpl.id, enabled: checked });
                        await fetchData(false);
                      } catch (e) { console.error(e); }
                      finally { setTogglingKey(null); }
                    }}
                  />
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Ad-hoc (Other) Fees Card */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-1.5">
        <div className="flex items-center justify-between mb-1">
          <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">Other (Ad-hoc)</div>
          {adHocTotal !== 0 && <span className={`text-xs font-semibold ${adHocTotal < 0 ? 'text-red-500' : 'text-amber-600'}`}>{adHocTotal < 0 ? '-' : ''}${Math.abs(adHocTotal).toFixed(2)}</span>}
        </div>

        {manualExtras.map((extra) => (
          <div key={extra.id} className="flex items-center justify-between py-2 px-3 bg-amber-50/70 rounded-lg group">
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-gray-900 truncate">{extra.type}</div>
              {extra.description && <div className="text-xs text-gray-400">{extra.description}</div>}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0 ml-3">
              <span className={`text-sm font-semibold ${parseFloat(String(extra.amount)) < 0 ? 'text-red-500' : 'text-gray-900'}`}>{parseFloat(String(extra.amount)) < 0 ? '-' : ''}${Math.abs(parseFloat(String(extra.amount))).toFixed(2)}</span>
              {isDraft && (
                <button
                  onClick={async () => {
                    setRemovingId(extra.id);
                    try {
                      // Delete this extra + matching manual extras on sibling payments
                      const idsToDelete = [extra.id];
                      for (const sib of allPayments) {
                        if (sib.id === payment.id) continue;
                        const sibExtras = Array.isArray(sib.extras) ? sib.extras : [];
                        const match = sibExtras.find(e => e.source === 'manual' && e.type === extra.type);
                        if (match) idsToDelete.push(match.id);
                      }
                      await Promise.all(idsToDelete.map(id => api.delete(`/draft-driver-payments/extras/${id}`)));
                      await fetchData(false);
                    } catch (e) { console.error(e); }
                    finally { setRemovingId(null); }
                  }}
                  disabled={removingId === extra.id}
                  className="p-0.5 text-gray-300 hover:text-red-500 rounded transition-colors disabled:opacity-50 opacity-0 group-hover:opacity-100"
                >
                  <X className={`w-3.5 h-3.5 ${removingId === extra.id ? 'animate-spin' : ''}`} />
                </button>
              )}
            </div>
          </div>
        ))}

        {isDraft && (
          <div className="pt-1.5 border-t border-dashed border-gray-200 mt-2">
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={addingAdHoc ? adHocDesc : ''}
                onChange={(e) => { setAddingAdHoc(true); setAdHocDesc(e.target.value); }}
                placeholder="Description"
                className="flex-1 min-w-0 px-2.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 placeholder:text-gray-300"
                style={{ height: 28, lineHeight: '28px' }}
                onKeyDown={async (e) => {
                  if (e.key === 'Enter' && adHocDesc && adHocAmount && !!parseFloat(adHocAmount)) {
                    setAddingAdHoc(true);
                    try { await Promise.all(allPaymentIds.map(pid => api.post(`/draft-driver-payments/${pid}/extras`, { type: adHocDesc, amount: parseFloat(adHocAmount) }))); setAdHocDesc(''); setAdHocAmount(''); await fetchData(false); }
                    catch (err) { console.error(err); }
                    finally { setAddingAdHoc(false); }
                  }
                }}
              />
              <input
                type="number"
                step={0.01}
                value={addingAdHoc ? adHocAmount : ''}
                onChange={(e) => { setAddingAdHoc(true); setAdHocAmount(e.target.value); }}
                placeholder="$0.00"
                className="w-24 px-2.5 text-sm text-right border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 placeholder:text-gray-300"
                style={{ height: 28 }}
              />
              <button
                onClick={async () => {
                  if (!adHocDesc || !adHocAmount || !parseFloat(adHocAmount)) return;
                  setAddingAdHoc(true);
                  try { await Promise.all(allPaymentIds.map(pid => api.post(`/draft-driver-payments/${pid}/extras`, { type: adHocDesc, amount: parseFloat(adHocAmount) }))); setAdHocDesc(''); setAdHocAmount(''); await fetchData(false); }
                  catch (err) { console.error(err); }
                  finally { setAddingAdHoc(false); }
                }}
                disabled={!adHocDesc || !adHocAmount || !parseFloat(adHocAmount)}
                className="p-1.5 text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100 transition-colors disabled:opacity-30 disabled:cursor-not-allowed flex-shrink-0"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {manualExtras.length === 0 && !isDraft && (
          <div className="text-sm text-gray-400 text-center py-2">No ad-hoc fees</div>
        )}
      </div>

      {/* Total Summary Card */}
      <div className="bg-white rounded-xl border-2 p-4" style={{ borderColor: `${accentColor}33` }}>
        <div className="flex items-center justify-between">
          <div>
            {(extraRate > 0 || adHocTotal > 0) && (
              <div className="text-xs text-gray-400 mb-1">
                {extraRate > 0 && <span>Extras ${(extraRate - adHocTotal).toFixed(2)}</span>}
                {extraRate > 0 && adHocTotal > 0 && <span> + </span>}
                {adHocTotal > 0 && <span>Ad-hoc ${adHocTotal.toFixed(2)}</span>}
              </div>
            )}
            <div className="text-xs font-medium text-gray-500 uppercase">Total Amount</div>
          </div>
          <div className="text-2xl font-bold text-gray-900">
            ${total.toFixed(2)}
          </div>
        </div>
      </div>
    </>
  );
}

/* ── Reusable Extra Row ── */

function ExtraRow({
  label, desc, graceLabel, detailDesc, amount, isActive, isDraft, isToggling, onToggle,
}: {
  label: string;
  desc?: string;
  graceLabel?: string;
  detailDesc?: string;
  amount: number;
  isActive: boolean;
  isDraft: boolean;
  isToggling: boolean;
  onToggle?: (checked: boolean) => void;
}) {
  return (
    <div className={cn(
      'flex items-center justify-between py-2.5 px-3 rounded-lg transition-colors',
      isActive ? 'bg-gray-50 hover:bg-gray-100' : 'bg-white opacity-50',
    )}>
      <div className="flex items-center gap-3 flex-1 min-w-0">
        {isDraft && onToggle ? (
          <Switch size="small" checked={isActive} loading={isToggling} onChange={onToggle} />
        ) : (
          <Lock className="h-3.5 w-3.5 text-gray-300 flex-shrink-0" />
        )}
        <div className="min-w-0">
          <div className={cn('text-sm font-medium', isActive ? 'text-gray-900' : 'text-gray-400')}>{label}</div>
          {desc && (
            <div className="text-xs text-gray-400 mt-0.5">
              {desc}
              {graceLabel && <span className="ml-1.5 text-amber-500">{graceLabel}</span>}
            </div>
          )}
          {detailDesc && <div className="text-[11px] text-blue-500 mt-0.5">{detailDesc}</div>}
        </div>
      </div>
      <span className={cn('text-sm font-semibold', isActive ? 'text-gray-900' : 'text-gray-300')}>
        ${amount.toFixed(2)}
      </span>
    </div>
  );
}
